import { getLocationCountry } from '@repo/common';
import { translateTexts } from '../../../api/career/career.api';

const translateText = async (text) => {
  if (!text || typeof text !== 'string' || !text.trim()) return text;
  const [translated] = await translateTexts([text]);
  return translated ?? text;
};

const fixKoreanLanguageSuffix = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text.replace(/인$/, '어');
};

const translateArray = async (arr, fields) => {
  if (!Array.isArray(arr) || arr.length === 0) return arr ?? [];
  return Promise.all(
    arr.map(async (item) => {
      if (!item) return item;
      if (typeof item === 'string') return await translateText(item);
      const translated = { ...item };
      for (const field of fields) {
        if (item[field] && typeof item[field] === 'string') {
          translated[field] = await translateText(item[field]);
        }
      }
      if (item.keyAchievements && Array.isArray(item.keyAchievements)) {
        translated.keyAchievements = await Promise.all(
          item.keyAchievements.map(async (a) => ({
            ...a,
            title: a.title ? await translateText(a.title) : a.title,
            description: a.description
              ? await translateText(a.description)
              : a.description,
          }))
        );
      }
      return translated;
    })
  );
};

export const translateResumeData = async (resumeData) => {
  const [experience, activity, summary] = await Promise.all([
    translateArray(resumeData.experience, [
      'jobTitle',
      'employmentType',
      'headline',
    ]),
    translateArray(resumeData.activity, ['title', 'description']),
    translateText(resumeData.summary),
  ]);

  const KOREAN_SKILLS_MAP = {
    Beginner: '일상 회화 가능',
    Intermediate: '일상 및 간단한 업무 소통 가능',
    Advanced: '비즈니스 대화 가능',
    Native: '원어민',
  };

  const profile = {
    ...resumeData.profile,
    koreanSkills:
      KOREAN_SKILLS_MAP[resumeData.profile?.koreanSkills] ??
      resumeData.profile?.koreanSkills,
    nationality: Array.isArray(resumeData.profile?.nationality)
      ? await Promise.all(resumeData.profile.nationality.map(translateText))
      : await translateText(resumeData.profile?.nationality),
    location: resumeData.profile?.location
      ? await translateText(
          `${getLocationCountry(resumeData.profile.location)}`
        )
      : `${resumeData.profile?.location}`,
  };

  const visa = await Promise.all(
    (resumeData.visa ?? []).map(async (v) => ({
      ...v,
      visaTypeText: v.visaTypeText
        ? await translateText(v.visaTypeText)
        : v.visaTypeText,
    }))
  );

  const translatedLanguages = await Promise.all(
    (resumeData.language ?? []).map(async (lang) => ({
      ...lang,
      language: fixKoreanLanguageSuffix(await translateText(lang.language)),
    }))
  );

  return {
    ...resumeData,
    profile,
    summary,
    education: resumeData.education,
    experience,
    skill: resumeData.skill,
    activity,
    visa,
    language: translatedLanguages,
    link: resumeData.link,
  };
};
